/**
 * Game Event Bus for Poker Reborn Effects Engine
 *
 * A type-safe pub/sub system for game events. The Effects Director subscribes
 * to these events to trigger visual effects without blocking game logic.
 *
 * Features:
 * - Type-safe event subscriptions via TypeScript generics
 * - Event history for replay (state recovery)
 * - Error handling in event handlers (never breaks game flow)
 * - Singleton access for global event coordination
 *
 * @see tasks/active/EFFECTS_ENGINE_IMPLEMENTATION_PLAN.md - Phase 1
 */

import type {
  GameEventName,
  GameEventPayload,
  RecordedEvent,
} from './GameEvents';
import { log } from '@/utils/log';

// =============================================================================
// TYPES
// =============================================================================

/** Event handler function type */
export type GameEventHandler<E extends GameEventName> = (data: GameEventPayload<E>) => void;

/** Internal handler wrapper with metadata */
interface HandlerEntry<E extends GameEventName> {
  handler: GameEventHandler<E>;
  once: boolean;
}

/** Configuration options for GameEventBus */
export interface GameEventBusConfig {
  /** Maximum number of events to keep in history (default: 100) */
  maxHistorySize: number;
  /** Whether to log events (default: false in production) */
  debugLogging: boolean;
}

// =============================================================================
// GAME EVENT BUS
// =============================================================================

/**
 * GameEventBus - Central event hub for the Effects Engine
 *
 * Game controllers emit events here, and the Effects Director subscribes
 * to trigger animations and visual effects.
 *
 * @example
 * ```typescript
 * // Subscribe to an event
 * gameEventBus.on('hand:start', (data) => {
 *   console.log(`Hand ${data.gameNumber} started!`);
 * });
 *
 * // Emit an event
 * gameEventBus.emit('hand:start', { gameNumber: 123, dealerSeat: 0 });
 *
 * // One-time subscription
 * gameEventBus.once('hand:end', (data) => {
 *   console.log('Hand ended, cleaning up...');
 * });
 * ```
 */
export class GameEventBus {
  private static instance: GameEventBus | null = null;

  /** Event handlers map: event name -> set of handler entries */
  private handlers: Map<GameEventName, Set<HandlerEntry<GameEventName>>> = new Map();

  /** Event history for replay */
  private history: RecordedEvent[] = [];

  /** Configuration */
  private config: GameEventBusConfig;

  /** Subscription ID counter for debugging */
  private subscriptionIdCounter = 0;

  private constructor(config?: Partial<GameEventBusConfig>) {
    this.config = {
      maxHistorySize: config?.maxHistorySize ?? 100,
      debugLogging: config?.debugLogging ?? false,
    };
  }

  // ===========================================================================
  // SINGLETON
  // ===========================================================================

  /**
   * Get the singleton GameEventBus instance
   */
  static getInstance(): GameEventBus {
    if (!GameEventBus.instance) {
      GameEventBus.instance = new GameEventBus();
    }
    return GameEventBus.instance;
  }

  /**
   * Reset the singleton instance (for testing)
   */
  static resetInstance(): void {
    if (GameEventBus.instance) {
      GameEventBus.instance.removeAllListeners();
      GameEventBus.instance.clearHistory();
    }
    GameEventBus.instance = null;
  }

  // ===========================================================================
  // SUBSCRIPTION METHODS (P1.1.3 - P1.1.5)
  // ===========================================================================

  /**
   * Subscribe to an event
   *
   * @param event - Event name to subscribe to
   * @param handler - Handler function called when event is emitted
   * @returns Unsubscribe function
   *
   * @example
   * ```typescript
   * const unsubscribe = gameEventBus.on('player:acted', (data) => {
   *   playChipSound(data.action);
   * });
   *
   * // Later: unsubscribe()
   * ```
   */
  on<E extends GameEventName>(
    event: E,
    handler: GameEventHandler<E>
  ): () => void {
    return this.addHandler(event, handler, false);
  }

  /**
   * Subscribe to an event once (auto-unsubscribes after first trigger)
   *
   * @param event - Event name to subscribe to
   * @param handler - Handler function called once
   * @returns Unsubscribe function (can cancel before event fires)
   *
   * @example
   * ```typescript
   * gameEventBus.once('hand:end', () => {
   *   clearTableAnimations();
   * });
   * ```
   */
  once<E extends GameEventName>(
    event: E,
    handler: GameEventHandler<E>
  ): () => void {
    return this.addHandler(event, handler, true);
  }

  /**
   * Unsubscribe from an event
   *
   * @param event - Event name
   * @param handler - The handler function to remove
   * @returns true if handler was found and removed
   *
   * @example
   * ```typescript
   * const handler = (data) => { ... };
   * gameEventBus.on('player:fold', handler);
   * // Later:
   * gameEventBus.off('player:fold', handler);
   * ```
   */
  off<E extends GameEventName>(
    event: E,
    handler: GameEventHandler<E>
  ): boolean {
    const eventHandlers = this.handlers.get(event);
    if (!eventHandlers) return false;

    // Find and remove the handler
    for (const entry of eventHandlers) {
      if (entry.handler === handler) {
        eventHandlers.delete(entry);
        if (this.config.debugLogging) {
          log.effects.debug`Unsubscribed from ${event}`;
        }
        return true;
      }
    }

    return false;
  }

  // ===========================================================================
  // EMIT (P1.1.6)
  // ===========================================================================

  /**
   * Emit an event to all subscribers
   *
   * Event handlers are called synchronously in subscription order.
   * Errors in handlers are caught and logged, never breaking the chain.
   *
   * @param event - Event name
   * @param data - Event payload
   * @returns Number of handlers that were called
   *
   * @example
   * ```typescript
   * gameEventBus.emit('hand:start', {
   *   gameNumber: 12345,
   *   dealerSeat: 2
   * });
   * ```
   */
  emit<E extends GameEventName>(event: E, data: GameEventPayload<E>): number {
    // Record in history
    this.recordEvent(event, data);

    if (this.config.debugLogging) {
      log.effects.debug`Event: ${event} ${JSON.stringify(data)}`;
    }

    const eventHandlers = this.handlers.get(event);
    if (!eventHandlers || eventHandlers.size === 0) {
      return 0;
    }

    let callCount = 0;
    const handlersToRemove: HandlerEntry<GameEventName>[] = [];

    // Call all handlers
    for (const entry of eventHandlers) {
      try {
        // Cast is safe because we maintain type consistency
        (entry.handler as GameEventHandler<E>)(data);
        callCount++;

        // Mark once handlers for removal
        if (entry.once) {
          handlersToRemove.push(entry);
        }
      } catch (error) {
        // Never let handler errors break the event chain
        log.effects.error`Error in ${event} handler: ${error}`;
        console.error(`GameEventBus: Error in handler for "${event}":`, error);
      }
    }

    // Remove once handlers after iteration
    for (const entry of handlersToRemove) {
      eventHandlers.delete(entry);
    }

    return callCount;
  }

  // ===========================================================================
  // HISTORY (P1.1.7 - P1.1.8)
  // ===========================================================================

  /**
   * Get event history
   *
   * @param filter - Optional event name to filter by
   * @returns Array of recorded events (newest last)
   *
   * @example
   * ```typescript
   * // Get all history
   * const allEvents = gameEventBus.getHistory();
   *
   * // Get only player action events
   * const actions = gameEventBus.getHistory('player:acted');
   * ```
   */
  getHistory<E extends GameEventName>(filter?: E): RecordedEvent<E>[] {
    if (filter) {
      return this.history.filter(
        (e): e is RecordedEvent<E> => e.event === filter
      );
    }
    return this.history as RecordedEvent<E>[];
  }

  /**
   * Get the most recent event of a specific type
   *
   * @param event - Event name
   * @returns Most recent event or undefined
   */
  getLastEvent<E extends GameEventName>(event: E): RecordedEvent<E> | undefined {
    for (let i = this.history.length - 1; i >= 0; i--) {
      const recorded = this.history[i];
      if (recorded?.event === event) {
        return recorded as RecordedEvent<E>;
      }
    }
    return undefined;
  }

  /**
   * Clear event history
   *
   * Called at hand end to prevent memory buildup.
   * Also useful for testing.
   */
  clearHistory(): void {
    this.history = [];
    if (this.config.debugLogging) {
      log.effects.debug`Event history cleared`;
    }
  }

  /**
   * Replay events from history to a handler
   *
   * Useful for state recovery when Effects Director initializes.
   *
   * @param handler - Handler to receive replayed events
   * @param filter - Optional event name filter
   */
  replayHistory<E extends GameEventName>(
    handler: (event: RecordedEvent<E>) => void,
    filter?: E
  ): void {
    const events = this.getHistory(filter);
    for (const event of events) {
      try {
        handler(event);
      } catch (error) {
        log.effects.error`Error replaying event ${event.event}: ${error}`;
      }
    }
  }

  // ===========================================================================
  // UTILITY METHODS
  // ===========================================================================

  /**
   * Remove all event listeners
   *
   * @param event - Optional specific event to clear. If omitted, clears all.
   */
  removeAllListeners(event?: GameEventName): void {
    if (event) {
      this.handlers.delete(event);
    } else {
      this.handlers.clear();
    }
  }

  /**
   * Get number of listeners for an event
   *
   * @param event - Event name
   * @returns Number of registered handlers
   */
  listenerCount(event: GameEventName): number {
    return this.handlers.get(event)?.size ?? 0;
  }

  /**
   * Get all event names that have listeners
   */
  getActiveEvents(): GameEventName[] {
    const active: GameEventName[] = [];
    for (const [event, handlers] of this.handlers) {
      if (handlers.size > 0) {
        active.push(event);
      }
    }
    return active;
  }

  /**
   * Check if an event has any listeners
   */
  hasListeners(event: GameEventName): boolean {
    return (this.handlers.get(event)?.size ?? 0) > 0;
  }

  /**
   * Get current history size
   */
  get historySize(): number {
    return this.history.length;
  }

  /**
   * Update configuration
   */
  configure(config: Partial<GameEventBusConfig>): void {
    if (config.maxHistorySize !== undefined) {
      this.config.maxHistorySize = config.maxHistorySize;
      this.trimHistory();
    }
    if (config.debugLogging !== undefined) {
      this.config.debugLogging = config.debugLogging;
    }
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  /**
   * Add a handler for an event
   */
  private addHandler<E extends GameEventName>(
    event: E,
    handler: GameEventHandler<E>,
    once: boolean
  ): () => void {
    // Get or create handler set for this event
    let eventHandlers = this.handlers.get(event);
    if (!eventHandlers) {
      eventHandlers = new Set();
      this.handlers.set(event, eventHandlers);
    }

    // Create entry
    const entry: HandlerEntry<E> = { handler, once };
    eventHandlers.add(entry as HandlerEntry<GameEventName>);

    this.subscriptionIdCounter++;
    if (this.config.debugLogging) {
      log.effects.debug`Subscribed to ${event} (${once ? 'once' : 'persistent'})`;
    }

    // Return unsubscribe function
    return () => {
      eventHandlers.delete(entry as HandlerEntry<GameEventName>);
    };
  }

  /**
   * Record an event in history
   */
  private recordEvent<E extends GameEventName>(
    event: E,
    data: GameEventPayload<E>
  ): void {
    const recorded: RecordedEvent<E> = {
      event,
      data,
      timestamp: Date.now(),
    };

    this.history.push(recorded as RecordedEvent);
    this.trimHistory();
  }

  /**
   * Trim history to max size
   */
  private trimHistory(): void {
    if (this.history.length > this.config.maxHistorySize) {
      const excess = this.history.length - this.config.maxHistorySize;
      this.history.splice(0, excess);
    }
  }
}

// =============================================================================
// SINGLETON EXPORT (P1.1.9)
// =============================================================================

/**
 * Singleton game event bus instance
 *
 * Use this for all event subscriptions and emissions.
 *
 * @example
 * ```typescript
 * import { gameEventBus } from '@/effects/events';
 *
 * // In Effects Director
 * gameEventBus.on('hand:start', (data) => {
 *   playDealAnimation(data.dealerSeat);
 * });
 *
 * // In Game Controller
 * gameEventBus.emit('hand:start', { gameNumber: 123, dealerSeat: 0 });
 * ```
 */
export const gameEventBus = GameEventBus.getInstance();
